import { DomainPricing } from '@/types';

/**
 * Formats a price with currency symbol
 */
export function formatPrice(price: number | null, currency: string = 'USD'): string {
  if (price === null || price === undefined) {
    return 'N/A';
  }

  const currencySymbols: Record<string, string> = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'CAD': 'C$',
    'AUD': 'A$'
  };

  const symbol = currencySymbols[currency] || currency;
  return `${symbol}${price.toFixed(2)}`;
}

/**
 * Returns the appropriate color class for pricing badge based on pricing data
 */
export function getPricingBadgeColor(pricing: DomainPricing | null | undefined): string {
  if (!pricing) {
    return 'bg-gray-100 text-gray-600 font-bold';
  }

  if (!pricing.purchasable) {
    return 'bg-red-100 text-red-700 font-bold';
  }

  if (pricing.premium) {
    return 'bg-yellow-100 text-yellow-900 font-bold';
  }

  if (pricing.purchasePrice === null || pricing.purchasePrice === undefined) {
    return 'bg-gray-100 text-gray-600 font-bold';
  }

  // Standard pricing - green for good value with high contrast
  return 'bg-green-100 text-green-800 font-bold';
}

/**
 * Checks if a domain is premium based on pricing data
 */
export function isPremiumDomain(pricing: DomainPricing | null | undefined): boolean {
  return pricing?.premium || false;
}

/**
 * Checks if a domain is purchasable based on pricing data
 */
export function isPurchasableDomain(pricing: DomainPricing | null | undefined): boolean {
  return pricing?.purchasable || false;
}

/**
 * Gets the display text for purchase type
 */
export function getPurchaseTypeText(pricing: DomainPricing | null | undefined): string {
  if (!pricing) return 'Unknown';

  switch (pricing.purchaseType) {
    case 'registration':
      return 'Registration';
    case 'renewal':
      return 'Renewal';
    case 'transfer':
      return 'Transfer';
    default:
      return 'Registration';
  }
}

/**
 * Gets a summary of pricing information for tooltips
 */
export function getPricingSummary(pricing: DomainPricing | null | undefined): string {
  if (!pricing) {
    return 'Pricing information unavailable';
  }

  const parts: string[] = [];

  if (pricing.purchasePrice !== null) {
    parts.push(`Purchase: ${formatPrice(pricing.purchasePrice, pricing.currency)}`);
  }

  if (pricing.renewalPrice !== null) {
    parts.push(`Renewal: ${formatPrice(pricing.renewalPrice, pricing.currency)}`);
  }

  if (pricing.transferPrice !== null) {
    parts.push(`Transfer: ${formatPrice(pricing.transferPrice, pricing.currency)}`);
  }

  if (parts.length === 0) {
    return 'No pricing data available';
  }

  return parts.join(' • ');
}

/**
 * Gets the primary price to display (purchase price or renewal price)
 */
export function getPrimaryPrice(pricing: DomainPricing | null | undefined): number | null {
  if (!pricing) return null;

  // Prefer purchase price, fallback to renewal price
  return pricing.purchasePrice ?? pricing.renewalPrice ?? null;
}

/**
 * Gets the registrar display name
 */
export function getRegistrarDisplayName(pricing: DomainPricing | null | undefined): string {
  if (!pricing) return 'Unknown';

  const registrarNames: Record<string, string> = {
    'name.com': 'Name.com',
    'godaddy': 'GoDaddy',
    'namecheap': 'Namecheap',
    'cloudflare': 'Cloudflare'
  };

  return registrarNames[pricing.registrar] || pricing.registrar;
}
